import { IMyWindow } from '@aca-new/app/shared/interfaces/my-window.interface';
import { WINDOW } from '@aca-new/app/shared/tokens/window.token';
import { DOCUMENT } from '@angular/common';
import { ElementRef, Inject, Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class AppScrollService {
  public constructor(
    @Inject(WINDOW) private readonly _window: IMyWindow,
    @Inject(DOCUMENT) private readonly _document: Document
  ) {}

  public getTopOffset(controlEl: Readonly<HTMLElement>, stickyTopOffset = 80): number {
    return controlEl.getBoundingClientRect().top + this._window.scrollY - stickyTopOffset;
  }

  /**
   * @param {string} selector - CSS selector for a sticky header element.
   * @param {number} fallback - Offset when the element is not in the DOM.
   * @returns {number} Measured sticky header height in pixels.
   */
  public getStickyElementHeight(selector: string, fallback = 80): number {
    const stickyElement = this._document.querySelector(selector);

    return stickyElement?.getBoundingClientRect().height ?? fallback;
  }

  public scrollToFirstInvalidControl(el: ElementRef, alternativeSelectors: string[] = []): void {
    const classes = [
      '.app-form-group--invalid',
      '.app-form-group__input__content .ng-invalid',
      '.supplier-information .ng-invalid',
      '.inspection-details .ng-invalid',
      ...alternativeSelectors,
    ];

    // Use requestAnimationFrame + setTimeout to ensure DOM updates and panel animations complete
    requestAnimationFrame((): void => {
      setTimeout((): void => {
        const firstInvalidControl: HTMLElement = el.nativeElement.querySelector(classes.join(','));

        if (!firstInvalidControl) {
          return;
        }

        firstInvalidControl.style.scrollMarginTop = '80px';
        firstInvalidControl.scrollIntoView({ behavior: 'smooth' });
        firstInvalidControl.style.scrollMarginTop = '';
      }, 300);
    });
  }

  public scrolledByClassName(selector: string = '.scroll-area', block: ScrollLogicalPosition = 'center'): void {
    this._document.querySelector(selector)?.scrollIntoView({
      block,
      inline: 'nearest',
      behavior: 'smooth',
    });
  }

  public scrollToElementById(elementId: string, block: ScrollLogicalPosition = 'center', stickyTopSelector?: string): void {
    const element = this._document.getElementById(elementId);

    if (!element) {
      return;
    }

    if (block === 'start') {
      const stickyTopOffset = stickyTopSelector ? this.getStickyElementHeight(stickyTopSelector) : 80;

      this._window.scrollTo({
        top: Math.max(0, this.getTopOffset(element, stickyTopOffset)),
        behavior: 'smooth',
      });

      return;
    }

    const elementRect = element.getBoundingClientRect();
    const viewportHeight = this._window.innerHeight;
    const elementTop = elementRect.top + this._window.scrollY;
    // Golden ratio inverse (1 - 0.618 = 0.382) for optimal reading position
    const goldenRatioOffset = viewportHeight * 0.382;
    const targetScrollPosition = elementTop - goldenRatioOffset;

    this._window.scrollTo({
      top: Math.max(0, targetScrollPosition),
      behavior: 'smooth',
    });
  }

  public scrolledToBottom(): void {
    this._window.scroll({
      top: document.body.scrollHeight,
      behavior: 'smooth',
    });
  }

  public scrollToTop(): void {
    this._window.scroll({
      top: 0,
      behavior: 'smooth',
    });
  }
}
